import { useId, useState, type ReactNode } from "react";
import "./criador.css";
import type { ClubKit, CrestPiece, CrestShapeData } from "@legacy-manager/engine";
import { useT, type Chave } from "../../i18n";
import { Panel } from "../atoms";
import { useDialogoDeAtalhos } from "../useAtalhos";
import type { EstiloDeFigura } from "./catalogo";
import { SeletorDeFigura } from "./SeletorDeFigura";

/**
 * O uniforme do clube criado: padrão, duas cores e uma peça no peito.
 *
 * **A geometria é a mesma de `ui/cosmeticos.tsx`**, copiada linha a linha: o
 * mesmo contorno de camisa, as mesmas listras em 3, 11, 19, 27 e 35, as mesmas
 * faixas em 6, 16, 26 e 36, o mesmo xadrez de oito por oito. O jogo tem duas
 * camisas, a comprada e a criada, e elas aparecem lado a lado no mesmo hub: se
 * as listras de uma forem mais grossas que as da outra, o clube criado parece
 * de outro jogo.
 *
 * O desenho foi copiado em vez de importado porque os tipos não batem, e nenhum
 * dos dois pode ceder. `KitParams` do cosmético não tem `solid`, que o clube
 * criado precisa ter, e tem `detail` para gola e punho, que o `ClubKit` do
 * motor não guarda. Importar exigiria mexer no cosmético, que é do catálogo
 * pago, para servir ao criador, que é grátis.
 *
 * **A gola e os punhos usam a segunda cor.** No cosmético eles têm cor própria;
 * aqui a terceira cor não existe, e mandar a pessoa escolher três cores para
 * uma camisa de 34 pixels seria pedir uma decisão que ninguém vê. De quebra é o
 * que dá emprego à segunda cor no padrão liso, onde ela não pinta mais nada.
 *
 * **Nada aqui escreve marcação**, pelo mesmo motivo do escudo: no online a
 * camisa do outro chega pela rede, e a peça do peito vira elemento de verdade a
 * partir de dado, nunca texto de SVG.
 */

/** Silhueta da camisa: ombros, mangas, corpo e a gola em recorte. */
const CAMISA = "M14 3 L7 5 L2 14 L8 19 L12 16 V37 H28 V16 L32 19 L38 14 L33 5 L26 3 A6.5 6.5 0 0 1 14 3 Z";

/**
 * A grade da camisa, quadrada, ao contrário da do escudo, que é 40 por 44.
 *
 * Exportada porque o avatar e o vestiário desenham o mesmo padrão em outra
 * escala: eles recebem esta caixa e a levam para cima do desenho deles. Sem a
 * constante, cada um repetiria o 40 na conta e uma grade nova quebraria os três
 * em lugares diferentes.
 */
export const LADO_DO_UNIFORME = 40;
const LADO = LADO_DO_UNIFORME;

/**
 * Onde a peça do peito fica, em centésimos da grade.
 *
 * Posição e tamanho são fixos, e é de propósito: o peito comporta uma figura
 * só, e mover ou redimensionar dentro dele só produziria camisa com o emblema
 * torto. Mover, girar e empilhar existem no escudo, onde quatro peças disputam
 * o mesmo espaço e a composição é o assunto.
 *
 * O valor cai abaixo da gola, que termina em 7, e acima da barra, que começa em
 * 37. Vale nos dois padrões que cortam a camisa ao meio: em `halves` e em
 * `sash` a peça fica sobre as duas cores, então ela nasce com cor de contraste
 * e não com a segunda cor.
 */
const PEITO = { x: 50, y: 50, size: 30, rotation: 0 } as const;

/**
 * Os dois tamanhos da prévia.
 *
 * 34 é o tamanho em que a camisa aparece de verdade, ao lado do escudo, e é o
 * que decide: é ali que o xadrez vira cinza e a figura do peito vira ponto. O
 * grande serve para conferir o desenho depois de escolhido, não para escolher.
 */
const TAMANHOS_DA_PREVIA: Array<{ rotulo: Chave; tamanho: number }> = [
  { rotulo: "criador.previa.cabecalho", tamanho: 34 },
  { rotulo: "criador.previa.grande", tamanho: 150 },
];

/**
 * Altura do catálogo na pilha de diálogos, no `zIndex` e no teclado.
 *
 * Um número só para os dois: o Esc fecha o que está por cima, e "por cima" é o
 * que a tela mostra. Duas constantes diferentes deixariam o Esc fechar o
 * diálogo de baixo assim que alguém mexesse em uma delas.
 */
const NIVEL = 30;

/** Os seis padrões, na ordem em que aparecem na tela. */
const PADROES: Array<ClubKit["pattern"]> = [
  "solid",
  "stripes",
  "hoops",
  "sash",
  "halves",
  "checks",
];

/**
 * Preto ou branco, o que se enxergar sobre a cor de fundo.
 *
 * Serve à peça que acaba de nascer. Uma figura que estreia invisível parece
 * defeito, e a pessoa mexe nas cores da camisa atrás de um erro que não existe.
 * Depois ela troca a cor no campo ao lado, e este palpite não volta a falar.
 *
 * Os pesos são os do brilho percebido: verde pesa mais que vermelho, que pesa
 * mais que azul. Um amarelo e um azul da mesma conta de média dariam o mesmo
 * resultado, e o amarelo precisa de tinta escura enquanto o azul precisa de
 * clara.
 *
 * Exportada porque o número do calção tem o mesmo problema: ele cai sobre a
 * segunda cor do clube, e num clube de calção branco um número branco some.
 */
export function contrasteCom(fundo: string): string {
  const vermelho = Number.parseInt(fundo.slice(1, 3), 16);
  const verde = Number.parseInt(fundo.slice(3, 5), 16);
  const azul = Number.parseInt(fundo.slice(5, 7), 16);
  if (!Number.isFinite(vermelho + verde + azul)) return "#ffffff";
  const brilho = (0.299 * vermelho + 0.587 * verde + 0.114 * azul) / 255;
  return brilho > 0.55 ? "#101010" : "#ffffff";
}

// ---------------------------------------------------------------------------
// Desenho
// ---------------------------------------------------------------------------

/**
 * Uma forma solta, na grade de origem dela.
 *
 * Repete o que `Escudo.tsx` faz porque lá a função é interna: exportá-la
 * amarraria o desenho da camisa ao arquivo do escudo, e os dois têm grade
 * diferente. São quinze linhas sem decisão nenhuma dentro.
 */
function Forma({ forma }: { forma: CrestShapeData }): ReactNode {
  if (forma.t === "p") return <path d={forma.d} />;
  if (forma.t === "c") return <circle cx={forma.cx} cy={forma.cy} r={forma.r} />;
  if (forma.t === "e") return <ellipse cx={forma.cx} cy={forma.cy} rx={forma.rx} ry={forma.ry} />;
  return (
    <rect x={forma.x} y={forma.y} width={forma.w} height={forma.h} rx={forma.rx} ry={forma.ry} />
  );
}

/**
 * A peça no peito, posicionada na grade da camisa.
 *
 * A conta é a do escudo com um ajuste: a camisa é quadrada, então os dois eixos
 * medem contra o mesmo `LADO` e nenhuma figura estica.
 */
export function PecaDoPeito({ peca }: { peca: CrestPiece }): ReactNode {
  const lado = (peca.size / 100) * LADO;
  const escala = lado / peca.grid;
  const meio = peca.grid / 2;

  return (
    <g
      transform={`translate(${(peca.x / 100) * LADO} ${(peca.y / 100) * LADO}) rotate(${peca.rotation}) scale(${escala}) translate(${-meio} ${-meio})`}
      fill={peca.outline ? "none" : peca.color}
      stroke={peca.outline ? peca.color : "none"}
      strokeWidth={peca.outline ? 2 : undefined}
      strokeLinecap={peca.outline ? "round" : undefined}
      strokeLinejoin={peca.outline ? "round" : undefined}
    >
      {peca.draw.map((forma, indice) => (
        <Forma key={indice} forma={forma} />
      ))}
    </g>
  );
}

/** O padrão que vai sobre a cor de fundo, já recortado por quem chamou. */
export function Padrao({ uniforme }: { uniforme: ClubKit }): ReactNode {
  const { pattern, secondary } = uniforme;

  if (pattern === "halves") {
    return <rect x="20" y="0" width="20" height="40" fill={secondary} />;
  }

  if (pattern === "sash") {
    return <path d="M-4 30 L26 -4 L36 4 L6 38 Z" fill={secondary} />;
  }

  if (pattern === "stripes") {
    return (
      <>
        {[3, 11, 19, 27, 35].map((x) => (
          <rect key={x} x={x} y="0" width="4" height="40" fill={secondary} />
        ))}
      </>
    );
  }

  if (pattern === "hoops") {
    return (
      <>
        {[6, 16, 26, 36].map((y) => (
          <rect key={y} x="0" y={y} width="40" height="5" fill={secondary} />
        ))}
      </>
    );
  }

  if (pattern === "checks") {
    const casas: ReactNode[] = [];
    for (let linha = 0; linha < 8; linha += 1) {
      for (let coluna = 0; coluna < 8; coluna += 1) {
        if ((linha + coluna) % 2 === 0) continue;
        casas.push(
          <rect
            key={`${linha}-${coluna}`}
            x={coluna * 5}
            y={linha * 5}
            width="5"
            height="5"
            fill={secondary}
          />,
        );
      }
    }
    return <>{casas}</>;
  }

  return null;
}

/**
 * A camisa do clube criado, do tamanho que quem chama pedir.
 *
 * Exportada porque o hub, a ficha do clube e o cartão de compartilhar precisam
 * dela sem carregar o editor junto, do mesmo jeito que `EscudoDoClube` serve a
 * tabela sem carregar o criador.
 *
 * A peça do peito entra dentro do recorte. Uma figura que passe da barra sai
 * cortada pela camisa em vez de flutuar ao lado dela, que é o que o desenho
 * promete.
 */
export function CamisaDoClube({
  kit,
  size = 34,
}: {
  kit: ClubKit;
  size?: number;
}): ReactNode {
  // Duas camisas na mesma tela não podem dividir o mesmo recorte, senão a
  // segunda herda o contorno da primeira.
  const recorte = `camisa-criada-${useId()}`;

  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${LADO} ${LADO}`}
      aria-hidden="true"
      style={{ flexShrink: 0 }}
    >
      <defs>
        <clipPath id={recorte}>
          <path d={CAMISA} />
        </clipPath>
      </defs>

      <g clipPath={`url(#${recorte})`}>
        <rect x="0" y="0" width={LADO} height={LADO} fill={kit.primary} />
        <Padrao uniforme={kit} />
        {kit.badge && <PecaDoPeito peca={kit.badge} />}
      </g>

      <path d={CAMISA} fill="none" stroke="rgba(0,0,0,0.45)" strokeWidth="1.4" strokeLinejoin="round" />
      {/* Gola e punhos ficam fora do recorte: são o acabamento, não o padrão. */}
      <path d="M14 3 A6.5 6.5 0 0 0 26 3" fill="none" stroke={kit.secondary} strokeWidth="2.6" />
      <path d="M4 16 L8 19" stroke={kit.secondary} strokeWidth="2.6" strokeLinecap="round" />
      <path d="M36 16 L32 19" stroke={kit.secondary} strokeWidth="2.6" strokeLinecap="round" />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Controles
// ---------------------------------------------------------------------------

/** Rótulo de seção e o que vem debaixo dele, com o respiro de sempre. */
function Secao({ rotulo, children }: { rotulo: string; children: ReactNode }): ReactNode {
  return (
    <div style={{ display: "grid", gap: 8 }}>
      <span className="label-muted">{rotulo}</span>
      {children}
    </div>
  );
}

/**
 * Uma cor, pelo seletor do sistema e pelo código.
 *
 * O campo de texto existe porque quem cria um clube costuma vir com a cor na
 * mão, copiada de algum lugar, e achá-la na roda do sistema é trabalho. O
 * estado é local e só sobe quando o código está inteiro: subir a cada tecla
 * mandaria `#2` para o uniforme no meio da digitação.
 */
function Cor({
  rotulo,
  valor,
  aoMudar,
}: {
  rotulo: string;
  valor: string;
  aoMudar: (valor: string) => void;
}): ReactNode {
  const [texto, definirTexto] = useState(valor);
  const completa = /^#[0-9a-fA-F]{6}$/.test(texto) ? texto : valor;

  return (
    <label style={{ display: "grid", gap: 4 }}>
      <span className="label-muted">{rotulo}</span>
      <span className="row" style={{ gap: 6 }}>
        <input
          type="color"
          value={completa}
          aria-label={rotulo}
          onChange={(evento) => {
            definirTexto(evento.target.value);
            aoMudar(evento.target.value);
          }}
          style={{
            width: 34,
            height: 30,
            padding: 2,
            border: "1px solid var(--border-strong)",
            borderRadius: "var(--radius-sm)",
            background: "var(--surface-2)",
          }}
        />
        <input
          type="text"
          value={texto}
          maxLength={7}
          spellCheck={false}
          onChange={(evento) => {
            definirTexto(evento.target.value);
            if (/^#[0-9a-fA-F]{6}$/.test(evento.target.value)) aoMudar(evento.target.value);
          }}
          style={{
            width: 82,
            minHeight: 30,
            padding: "0 7px",
            fontFamily: "var(--font-mono)",
            fontSize: 12,
            color: "var(--text)",
            background: "var(--surface-2)",
            border: "1px solid var(--border-strong)",
            borderRadius: "var(--radius-sm)",
          }}
        />
      </span>
    </label>
  );
}

/**
 * Os seis padrões como seis camisas, e não como seis palavras.
 *
 * O padrão é escolha visual: "faixa" e "metades" descrevem a mesma coisa para
 * quem nunca viu as duas, e nas cores que a pessoa já escolheu a diferença se
 * decide num relance. O nome fica junto porque o botão precisa de nome acessível
 * e porque xadrez em 24 pixels vira textura cinza.
 */
function EscolhaDePadrao({
  uniforme,
  aoMudar,
}: {
  uniforme: ClubKit;
  aoMudar: (uniforme: ClubKit) => void;
}): ReactNode {
  const t = useT();

  return (
    <div
      role="group"
      style={{ display: "flex", flexWrap: "wrap", gap: 6 }}
    >
      {PADROES.map((padrao) => {
        const atual = padrao === uniforme.pattern;
        return (
          <button
            key={padrao}
            aria-pressed={atual}
            onClick={() => aoMudar({ ...uniforme, pattern: padrao })}
            style={{
              display: "grid",
              justifyItems: "center",
              gap: 3,
              padding: "6px 8px",
              minHeight: 0,
              fontSize: 10.5,
              lineHeight: 1.2,
              background: atual ? "var(--surface-3)" : "var(--surface-2)",
              borderColor: atual ? "var(--primary)" : "var(--border-strong)",
            }}
          >
            {/* A amostra não leva a peça do peito: o que se compara aqui é o
                padrão, e a mesma figura em seis botões só vira ruído. */}
            <CamisaDoClube kit={{ ...uniforme, pattern: padrao, badge: null }} size={30} />
            {t(`criador.uniforme.padroes.${padrao}`)}
          </button>
        );
      })}
    </div>
  );
}

/**
 * A peça sozinha, sobre a cor de fundo da camisa.
 *
 * O quadrado mostra a figura maior do que ela fica no peito, e não uma camisa
 * em miniatura: a prévia já é a camisa, e repeti-la aqui esconderia justamente
 * o que este canto controla, que é qual figura é e de que cor ela está.
 *
 * O fundo é a cor principal do uniforme porque a pergunta que se faz olhando
 * para cá é se a peça se enxerga por cima dela.
 */
function AmostraDaPeca({ uniforme, peca }: { uniforme: ClubKit; peca: CrestPiece }): ReactNode {
  return (
    <svg width={44} height={44} viewBox={`0 0 ${LADO} ${LADO}`} aria-hidden="true">
      <rect x="0" y="0" width={LADO} height={LADO} rx="3" fill={uniforme.primary} />
      <PecaDoPeito peca={{ ...peca, x: 50, y: 50, rotation: 0, size: 76 }} />
    </svg>
  );
}

/**
 * A peça do peito: escolher, tingir ou tirar.
 *
 * O quadrado é o botão que abre o catálogo, e não um enfeite com um botão ao
 * lado: ele já mostra o que está lá, e clicar no que se quer trocar é o gesto
 * que a pessoa tenta primeiro.
 *
 * A cor mora aqui, e não junto das cores da camisa, porque é cor de peça e não
 * de uniforme: sem ela, uma figura branca numa camisa branca some e parece que
 * a escolha não pegou.
 */
function PecaNoPeito({
  uniforme,
  aoMudar,
  aoAbrirCatalogo,
}: {
  uniforme: ClubKit;
  aoMudar: (uniforme: ClubKit) => void;
  aoAbrirCatalogo: () => void;
}): ReactNode {
  const t = useT();
  const peca = uniforme.badge;

  return (
    <div className="row" style={{ gap: 10, flexWrap: "wrap", alignItems: "flex-end" }}>
      <button
        aria-label={t("criador.catalogo.titulo")}
        title={t("criador.catalogo.titulo")}
        onClick={aoAbrirCatalogo}
        style={{
          width: 58,
          height: 58,
          display: "grid",
          placeItems: "center",
          padding: 0,
          fontSize: 10.5,
          color: "var(--text-dim)",
          borderStyle: peca ? "solid" : "dashed",
        }}
      >
        {peca ? <AmostraDaPeca uniforme={uniforme} peca={peca} /> : t("criador.uniforme.semPeca")}
      </button>

      {peca && (
        <>
          <Cor
            rotulo={t("criador.peca.cor")}
            valor={peca.color}
            aoMudar={(color) => aoMudar({ ...uniforme, badge: { ...peca, color } })}
          />
          <button
            className="ghost"
            onClick={() => aoMudar({ ...uniforme, badge: null })}
            style={{ minHeight: 30, padding: "4px 10px", fontSize: 11.5 }}
          >
            {t("criador.peca.remover")}
          </button>
        </>
      )}
    </div>
  );
}

/**
 * As duas prévias, em cima de tudo.
 *
 * Ficam antes dos controles, e não depois, porque toda mudança daqui para
 * baixo se julga por elas: num celular, com a prévia no rodapé, trocar o padrão
 * mexeria numa camisa que está fora da tela.
 */
function Previas({ uniforme }: { uniforme: ClubKit }): ReactNode {
  const t = useT();

  return (
    <div className="row" style={{ gap: 20, alignItems: "flex-end", flexWrap: "wrap" }}>
      {TAMANHOS_DA_PREVIA.map(({ rotulo, tamanho }) => (
        <div key={rotulo} style={{ display: "grid", gap: 6, justifyItems: "center" }}>
          <CamisaDoClube kit={uniforme} size={tamanho} />
          <span className="label-muted">{t(rotulo)}</span>
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Editor
// ---------------------------------------------------------------------------

/**
 * O editor de uniforme, com o estado inteiro na mão de quem chama.
 *
 * Igual ao editor de escudo, e pelo mesmo motivo: o uniforme é um pedaço do
 * clube que está sendo criado, e quem guarda o clube é a tela de criação. Um
 * estado interno aqui daria duas versões do mesmo uniforme, e a hora de elas
 * discordarem seria justo a hora de criar o clube.
 */
export function EditorDeUniforme({
  uniforme,
  aoMudar,
}: {
  uniforme: ClubKit;
  aoMudar: (uniforme: ClubKit) => void;
}): ReactNode {
  const t = useT();

  /*
   * O catálogo abre por cima, e não ao lado.
   *
   * São 1240 figuras com busca e categoria: encaixadas na coluna do editor,
   * elas ficariam com quatro por linha e empurrariam a prévia para fora da
   * tela, que é justamente o que se olha enquanto se escolhe a figura.
   */
  const [catalogo, definirCatalogo] = useState(false);

  /*
   * A aba do catálogo mora aqui porque o seletor não a guarda.
   *
   * É o que faz a escolha sobreviver a fechar e reabrir: quem estava vendo
   * contornos e fechou sem escolher volta para os contornos, e não para a
   * primeira aba com o traço que já tinha descartado.
   */
  const [estilo, definirEstilo] = useState<EstiloDeFigura>("cheios");

  return (
    <Panel title={t("criador.uniforme.titulo")}>
      {/*
        A mesma divisão do editor de escudo, e pelo mesmo motivo.

        Empilhado, tudo aqui ficava encostado na borda esquerda com metade do
        painel vazia ao lado, e trocar a cor empurrava a camisa para cima. As
        classes vêm de `criador.css` porque é o mesmo desenho: quem mexe num
        dos dois editores não pode achar que entrou em outro programa.
      */}
      <div className="criador-corpo">
        <div className="criador-lado">
          <Previas uniforme={uniforme} />
        </div>

        <div className="criador-coluna">
          <Secao rotulo={t("criador.uniforme.padrao")}>
            <EscolhaDePadrao uniforme={uniforme} aoMudar={aoMudar} />
          </Secao>

          <Secao rotulo={t("criador.uniforme.cores")}>
            <div className="row" style={{ gap: 14, flexWrap: "wrap" }}>
              <Cor
                rotulo={t("criador.uniforme.corPrincipal")}
                valor={uniforme.primary}
                aoMudar={(primary) => aoMudar({ ...uniforme, primary })}
              />
              <Cor
                rotulo={t("criador.uniforme.corSecundaria")}
                valor={uniforme.secondary}
                aoMudar={(secondary) => aoMudar({ ...uniforme, secondary })}
              />
            </div>
          </Secao>

          <Secao rotulo={t("criador.uniforme.peito")}>
            <PecaNoPeito
              uniforme={uniforme}
              aoMudar={aoMudar}
              aoAbrirCatalogo={() => definirCatalogo(true)}
            />
          </Secao>
        </div>
      </div>

      {catalogo && (
        <CatalogoAberto
          uniforme={uniforme}
          estilo={estilo}
          aoTrocarDeEstilo={definirEstilo}
          aoMudar={aoMudar}
          aoFechar={() => definirCatalogo(false)}
        />
      )}
    </Panel>
  );
}

/**
 * O catálogo por cima da tela, e a saída dele.
 *
 * Fica em componente próprio porque o registro no teclado é um gancho, e gancho
 * não pode nascer dentro de um `&&`: separado assim, ele entra quando o
 * catálogo abre e sai quando ele fecha, sem condicional no meio.
 */
function CatalogoAberto({
  uniforme,
  estilo,
  aoTrocarDeEstilo,
  aoMudar,
  aoFechar,
}: {
  uniforme: ClubKit;
  estilo: EstiloDeFigura;
  aoTrocarDeEstilo: (estilo: EstiloDeFigura) => void;
  aoMudar: (uniforme: ClubKit) => void;
  aoFechar: () => void;
}): ReactNode {
  const t = useT();

  // O nível é o mesmo `zIndex` da caixa: é assim que o Esc sabe qual diálogo
  // está por cima quando o criador abre outro por cima deste.
  useDialogoDeAtalhos({ nome: "Figuras do uniforme", nivel: NIVEL, aoFechar });

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: NIVEL,
        display: "grid",
        placeItems: "center",
        padding: "var(--gutter)",
        background: "rgba(4, 6, 5, 0.72)",
      }}
    >
      <div
        className="card"
        style={{
          width: "min(560px, 100%)",
          display: "grid",
          gap: 10,
          padding: 14,
          background: "var(--surface)",
        }}
      >
        {/*
          O seletor já desenha o próprio rótulo, as abas e a caixa de rolagem,
          então a moldura só acrescenta o que falta para sair daqui. Um segundo
          título por cima do dele seria a mesma palavra duas vezes.
        */}
        <SeletorDeFigura
          estilo={estilo}
          aoTrocarDeEstilo={aoTrocarDeEstilo}
          aoEscolher={(figura) => {
            /*
             * A peça chega do catálogo com a posição, o tamanho e a cor que
             * servem ao escudo. No peito nada disso vale: o lugar é fixo e a
             * cor precisa aparecer sobre esta camisa, e não sobre um fundo
             * qualquer. Trocar de figura preserva a cor que já tinha sido
             * escolhida à mão.
             */
            aoMudar({
              ...uniforme,
              badge: {
                ...figura,
                x: PEITO.x,
                y: PEITO.y,
                size: PEITO.size,
                rotation: PEITO.rotation,
                color: uniforme.badge?.color ?? contrasteCom(uniforme.primary),
              },
            });
            aoFechar();
          }}
        />

        <button className="ghost" onClick={aoFechar} style={{ justifySelf: "center" }}>
          {t("comum.fechar")}
        </button>
      </div>
    </div>
  );
}
